// Admin navigation — THE single source of truth for admin destinations.
//
// ONE tree, two levels: a group holds pages, a page belongs to exactly one
// group. Everything that navigates derives from it — the sidebar, the
// breadcrumbs, the admin homepage boxes — so none of them can drift.
//
// Groups are cut by SUBJECT, in the order the platform works: who is on it,
// what it reads, what could happen to them, what it predicts, what goes out,
// then the machinery. Cutting some groups by subject and others by verb is
// what produced "Data" — 17 of 45 pages, which is not a group but the pages
// nobody filed. There is no group for "things that run": a run belongs with
// the subject it acts on, which is why Generate Company Events sits with the
// event library and Model Gap Research with the models.
//
// It replaces three hand-maintained lists (Oct 2026). `adminSidebarItems`,
// `adminHomeBoxes` and `adminMoreTools` each held their own copy of the
// taxonomy and had diverged: 17 destinations reachable from a homepage box
// were missing from the sidebar list, and since breadcrumbs match on that
// list, those pages rendered a single "Admin" crumb with no group. Five
// routes carried two different labels, six sat in two different groups.
// /admin/all-tools existed only to render the demoted third list and went
// with it — a "more tools" tier is the same drift wearing a different hat.
//
// Shapes:
//   group — { id, label, icon, blurb, permission, settingsTab?, items[] }
//   item  — { id, label, icon, route, permission, settingsTab? }
//
// `settingsTab` (string or array) names the /settings category or categories
// a page or group OWNS. It is what SettingsCogLink renders a cog from, so a
// page gets a deep link to its own configuration by one field edit here. A
// group names the categories it is ABOUT; the cog opens the first and its
// tooltip lists the rest.
//
// To move a page, move it. To add one, add it in a group. There is nowhere
// else to update.

// The admin landing page. Not a group member — it is the root the
// breadcrumbs hang off.
export const adminOverview = {
  id: 'admin-overview',
  label: 'Overview',
  icon: 'gauge-high',
  route: '/dashboard/admin',
  permission: 'admin:read',
  settingsTab: 'housekeeping',
}

export const adminTree = [
  {
    // The platform's own pipeline is the order: who is on it, what it reads,
    // what could happen to them, what it predicts, what goes out.
    id: 'companies',
    label: 'Companies',
    icon: 'building',
    blurb: 'Who is on the platform',
    permission: 'admin:read',
    items: [
      { id: 'companies-admin', label: 'Manage Companies', icon: 'building', route: '/admin/companies', permission: 'company:write', settingsTab: ['onboarding', 'documents'] },
      { id: 'client-onboarding', label: 'Client Onboarding', icon: 'file-import', route: '/onboarding', permission: 'company:write', settingsTab: 'onboarding' },
      { id: 'company-export', label: 'Company Import / Export', icon: 'file-export', route: '/admin/company-export', permission: 'admin:read' },
    ],
  },
  {
    id: 'evidence',
    label: 'Evidence',
    icon: 'book-open',
    blurb: 'What it reads',
    permission: 'admin:read',
    // Heir to the old Data box's cog. Which of Companies / Evidence /
    // Events the `data` settings category really belongs to is a guess.
    settingsTab: 'data',
    items: [
      { id: 'sources', label: 'Sources Registry', icon: 'book-open', route: '/admin/sources', permission: 'sources:read' },
      { id: 'signal-ledger', label: 'Signal Ledger', icon: 'wave-square', route: '/admin/signal-ledger', permission: 'admin:read', settingsTab: 'signal_ledger' },
      { id: 'news', label: 'Global News', icon: 'newspaper', route: '/news', permission: 'admin:read' },
      // spec 098 — the portfolio-scoped news agent. Its own entry rather
      // than a tab on Global News: that page reads the global scan, this
      // one configures a sweep and mints EOIs from it.
      { id: 'news-agent', label: 'News Agent', icon: 'robot', route: '/admin/news-agent', permission: 'admin:read' },
    ],
  },
  {
    id: 'events',
    label: 'Events & Exposure',
    icon: 'diagram-project',
    blurb: 'What could happen, and to whom',
    permission: 'admin:read',
    items: [
      // ⚠ ONE MARKET, ONE PAGE — and it replaced three. `/admin/sectors` was a
      // tree with eight tabs, four of which belonged to sector insights and
      // left with it; `/admin/v3/events` was the same tree again with the
      // events beside it, deep-linking back to the first to edit a scope
      // sentence; `/admin/v3/generate` ran the company exposure pipeline that
      // the Hub's own company-events run now writes the same two tables from.
      // The index compares the markets, a market page is the work.
      { id: 'sectors', label: 'Sectors', icon: 'industry', route: '/admin/sectors', permission: 'admin:read' },
      // Beside Sectors, not beside Company Import / Export: the thing being
      // moved is a market and its research, and the two transfers share no
      // machinery (a sector has no company on it, so it is a sibling service).
      { id: 'sector-transfer', label: 'Sector Import / Export', icon: 'file-export', route: '/admin/sector-transfer', permission: 'admin:read' },
      // ⚠ NOT UNDER A SECTOR. A generic event names no market and reaches every
      // company, so it has no scope sentence, no members, no research and no
      // profile — four of the five things a market page is about. On the sector
      // tree it read as "a sector called Generic". The archived list lives here
      // too, because an archived event leaves every read and needs one home
      // whatever scope it was written at.
      { id: 'generic-events', label: 'Generic events', icon: 'diagram-project', route: '/admin/generic-events', permission: 'admin:read' },
      // ⚠ THE ONLY SURFACE THAT READS ACROSS ALL THREE. Generic events, a
      // market's events, the Hub's company tabs and the Runner's rail each show
      // one slice and none of them searches a description — so "which models do
      // we have about X" had no answer anywhere. It is here rather than under
      // Models because the subject is the EVENT, not the forecast on it.
      { id: 'event-search', label: 'Search events & EOIs', icon: 'magnifying-glass', route: '/admin/event-search', permission: 'admin:read' },
      // ⚠ A DESTINATION, NOT A BUTTON ON ANOTHER PAGE. Writing an event now also
      // settles how it is carried and who it applies to, which is four steps
      // rather than a panel — and `middleware/admin.global.ts` takes a page's
      // permission from its nav entry, so a page with no entry here would fall
      // back to a blanket `admin:read` chosen by nobody. The write itself is
      // Admin-only at the endpoint; this is the gate on reaching the form.
      { id: 'event-new', label: 'Write an event', icon: 'pen-to-square', route: '/admin/events/new', permission: 'admin:read' },
      // Step 1 research questions, grouped by company assessment report.
      { id: 'v3-questions', label: 'Company Assessment Questions Editor', icon: 'circle-question', route: '/admin/v3/questions', permission: 'admin:read' },
      { id: 'exposure-questions', label: 'Exposure Questions Editor', icon: 'circle-question', route: '/admin/exposure-questions', permission: 'admin:read' },
      { id: 'risk-questions', label: 'Risk Questions Editor', icon: 'circle-question', route: '/admin/risk-questions', permission: 'admin:read' },
      // The v2 block, labelled and last. These three run on the pre-v3
      // `riskQuestions` / `labelMapping` services, NOT on the company risks and
      // exposures pipelines the two editors above belong to — which carry almost
      // the same names, which is why the version is in the label. Rename Risk
      // Types is v2-era but not dead: its labels still render on live v5
      // surfaces (the EOI viewer, the alert cards).
      { id: 'risks', label: 'Risks Editor (v2)', icon: 'list-check', route: '/labs/risk-questions', permission: 'admin:read' },
      { id: 'labels', label: 'Rename Risk Types (v2)', icon: 'tags', route: '/label-mapping', permission: 'admin:read' },
      { id: 'risks-export', label: 'Risk Questions Import / Export (v2)', icon: 'file-export', route: '/admin/risks-export', permission: 'admin:read' },
    ],
  },
  {
    // The whole model lifecycle in one place: the research that finds the
    // gap, the linkage, what it forecasts and how well it did.
    id: 'models',
    label: 'Models & Forecasts',
    icon: 'chart-line',
    blurb: 'What it predicts, and how well',
    permission: 'admin:read',
    items: [
      { id: 'model-research', label: 'Model Gap Research', icon: 'flask', route: '/admin/model-research', permission: 'admin:read' },
      { id: 'model-linkage', label: 'Model Linkage', icon: 'link', route: '/admin/model-linkage', permission: 'admin:read' },
      // The v3 model TYPES: every number a forecast is computed from.
      // Internal by design — signal exploration is for David and Jason,
      // so there is no RM-facing route behind it.
      { id: 'forecast-models', label: 'Forecast Models (v3)', icon: 'sliders', route: '/admin/forecast-models', permission: 'admin:read' },
      // The same model watched running: one event's periods as a pipeline.
      // Beside the editor rather than inside it — the editor is where you
      // change a number, this is where you find out which one is wrong.
      { id: 'forecast-runner', label: 'Forecast Runner (v3)', icon: 'stopwatch', route: '/admin/forecast-runner', permission: 'admin:read' },
      // The SECOND class of v3 model: an event about a number somebody else
      // published, answered with a band in that series' own unit rather than a
      // 0-100 score. Its own entry because it is its own engine and its own
      // table — the ladder's two pages cannot show a band, and folding them
      // together is the "dogs dinner of branches" this was built to avoid.
      { id: 'series-model-runs', label: 'Published Series (v3)', icon: 'chart-line', route: '/admin/series-model-runs', permission: 'admin:read' },
      { id: 'models', label: 'Forecasting', icon: 'chart-bar', route: '/models', permission: 'models:read' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports & Alerts',
    icon: 'file-lines',
    blurb: 'What goes out',
    permission: 'admin:read',
    items: [
      // ⚠ THE IMPACT BRIEF'S OWN ADMIN, and first in the group because it is
      // the report the product is built around. Report Definitions and Report
      // Layout below are about the STRUCTURED company reports, a different
      // pipeline that happens to share the word. The seven prompt rows an
      // impact brief is written from are edited here; the cog opens the
      // settings section the same page already writes.
      { id: 'impact-reports', label: 'Impact Reports', icon: 'file-lines', route: '/admin/impact-reports', permission: 'prompts:write', settingsTab: 'impact' },
      { id: 'report-definitions', label: 'Report Definitions', icon: 'calculator', route: '/admin/report-definitions', permission: 'admin:read' },
      // Layout is a separate view on the same record: headings, order and
      // visibility are a different job from what a fact means, and a
      // different person does it.
      { id: 'report-layout', label: 'Report Layout', icon: 'table', route: '/admin/report-layout', permission: 'admin:read' },
      // Doozies is tonight's worklist; Doozy Watch is the same judge over
      // time — every flag either judge raised, including the ones the
      // filters dropped.
      { id: 'doozy-watch', label: 'Doozy Watch', icon: 'chart-line', route: '/doozy-watch', permission: 'admin:read' },
      // Judge Watch is the same grid one layer out: Doozy judges tonight's
      // ALERTS, this judges the REPORTS — analysis, risks, exposures, events
      // and facts — as their own quality and adversarial judges scored them.
      // It keeps its avatar-menu entry as "{portfolio} health"; this is a
      // second door, not a move, because the two are read in different moods.
      { id: 'judge-watch', label: 'Judge Watch', icon: 'scale-balanced', route: '/judge-watch', permission: 'admin:read' },
      // Doozy and Judge watch score what went out; this is the step BEFORE —
      // why anything went out at all. The score engine's own configuration had
      // no surface in the app between 31 Jul 2026, when the editable Impact
      // Matrix page was removed, and this page: its dials were readable only
      // by querying the database. Read-only, deliberately — retuning is a
      // migration, which is its own audit trail.
      { id: 'alert-scoring', label: 'Alert Scoring', icon: 'wave-square', route: '/admin/alert-scoring', permission: 'admin:read' },
      // The book behind Alert Scoring's counts: one row per EOI scored on a
      // night, banded by what became of it. Alert Scoring answers whether a
      // dial is doing anything; this answers what the whole night did — the
      // trips and, as the denominator under them, everything that did not.
      // ⚠ The route keeps its old spelling on purpose. `admin.global.ts`
      // takes a page's permission from THIS entry, so renaming the route
      // means renaming the page file and the entry in step or the guard
      // falls back to `admin:read` by accident rather than by decision.
      { id: 'trip-ledger', label: 'Score Engine Ledger', icon: 'list-check', route: '/admin/trip-ledger', permission: 'admin:read' },
      { id: 'portfolio-radar', label: 'Portfolio Radar Data', icon: 'bullseye', route: '/admin/portfolio-radar', permission: 'admin:read', settingsTab: 'housekeeping' },
    ],
  },
  {
    // How the intelligence is instructed and what it costs — as distinct
    // from the forecast models it produces, which are their own group.
    id: 'ai',
    label: 'AI',
    icon: 'microchip',
    blurb: 'How the intelligence is configured and what it costs',
    permission: 'admin:read',
    settingsTab: 'ai_models',
    items: [
      { id: 'prompts', label: 'Prompts Editor', icon: 'file-code', route: '/admin/prompts', permission: 'prompts:write' },
      { id: 'prompts-export', label: 'Prompts Import / Export', icon: 'file-export', route: '/admin/prompts-export', permission: 'prompts:read' },
      { id: 'llm-usage', label: 'LLM & AI Usage', icon: 'chart-area', route: '/admin/llm-usage', permission: 'admin:read' },
      // Audit and debug are about the AI, not about the machine — they sat
      // in Operations because the demoted list had nowhere else to put them.
      { id: 'llm-audit', label: 'LLM Audit Log', icon: 'file-lines', route: '/admin/llm-audit', permission: 'admin:read' },
      { id: 'rag-debug', label: 'RAG Debug', icon: 'database', route: '/admin/rag-debug', permission: 'settings:read', settingsTab: 'rag' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: 'server',
    blurb: 'Is the machine healthy right now',
    permission: 'admin:read',
    settingsTab: 'operations',
    items: [
      { id: 'pipeline-queue', label: 'Pipeline Queue', icon: 'layer-group', route: '/admin/pipeline-queue', permission: 'admin:read', settingsTab: 'pipelines' },
      { id: 'recent-jobs', label: 'Recent Jobs', icon: 'file-lines', route: '/admin/pipeline-queue/recent', permission: 'admin:read', settingsTab: 'system' },
      { id: 'provider-status', label: 'Provider Status', icon: 'heartbeat', route: '/admin/provider-status', permission: 'settings:read', settingsTab: 'reliability' },
      { id: 'settings', label: 'Settings', icon: 'sliders', route: '/settings', permission: 'admin:read' },
    ],
  },
  {
    id: 'access',
    label: 'Access',
    icon: 'users',
    blurb: 'Who can see and do what',
    permission: 'admin:read',
    items: [
      { id: 'users', label: 'User Management', icon: 'users', route: '/admin/users', permission: 'users:read' },
      { id: 'roles', label: 'Role Management', icon: 'key', route: '/admin/roles', permission: 'users:read' },
      { id: 'portfolios', label: 'Portfolios', icon: 'briefcase', route: '/admin/portfolios', permission: 'admin:read' },
      { id: 'ai-connections', label: 'AI Connections', icon: 'robot', route: '/admin/ai-connections', permission: 'mcp:manage' },
      { id: 'email-templates', label: 'Email Templates', icon: 'envelope', route: '/admin/email-templates', permission: 'email_templates:read' },
      // Not access control, and it knows it. Kept here because a group for
      // one page is worse than the misfit.
      { id: 'design-system', label: 'Design System', icon: 'sliders', route: '/design-system', permission: 'admin:read' },
    ],
  },
  {
    id: 'feedback',
    label: 'Feedback',
    icon: 'comments',
    blurb: 'How it’s landing with users',
    permission: 'admin:read',
    items: [
      { id: 'usage', label: 'Usage Analytics', icon: 'heart-pulse', route: '/admin/usage', permission: 'admin:read' },
      { id: 'feedback', label: 'Feedback Leaderboard', icon: 'thumbs-up', route: '/admin/feedback', permission: 'admin:read' },
      { id: 'site-feedback', label: 'Site Feedback', icon: 'comments', route: '/admin/site-feedback', permission: 'site_feedback:read' },
    ],
  },
]

// Flat sidebar/breadcrumb view of the same tree: Overview, then each group
// as a section divider followed by its pages. DERIVED, never authored — a
// second hand-written list is what this file just stopped having.
// `view: 'admin'` is what the layout filters the sidebar on.
export const adminSidebarItems = [
  { ...adminOverview, view: 'admin' },
  ...adminTree.flatMap((group) => [
    { type: 'section', label: group.label, permission: group.permission, view: 'admin' },
    ...group.items.map((item) => ({ ...item, view: 'admin' })),
  ]),
]

// Settings categories owned by pages that have NO admin-nav entry, so there is
// nowhere to hang a `settingsTab` field. Both are user-facing surfaces reached
// from outside the admin area: the chat page, and Client Settings, which owns
// the Teams integration outright (the `integrations` tab is a pointer to it).
export const settingsTabsByRoute = {
  '/chat': 'chat',
  '/client-settings': 'integrations',
}

/**
 * Settings categories a route owns, as an array (empty when it owns none).
 *
 * @param {string} route
 * @returns {string[]}
 */
export function settingsTabsForRoute(route) {
  if (!route) return []
  const found = []
  const push = (value) => {
    for (const tab of Array.isArray(value) ? value : [value]) {
      if (tab && !found.includes(tab)) found.push(tab)
    }
  }

  for (const entry of [adminOverview, ...adminTree.flatMap((g) => g.items)]) {
    if (entry.route === route && entry.settingsTab) push(entry.settingsTab)
  }
  if (settingsTabsByRoute[route]) push(settingsTabsByRoute[route])
  return found
}

/**
 * The group a route belongs to, or null. The breadcrumb's section crumb and
 * the sidebar's open group both come from here.
 *
 * Longest matching route wins, so nested destinations resolve to their own
 * entry rather than a parent route.
 *
 * @param {string} route
 * @returns {{ group: object, item: object }|null}
 */
export function adminLocate(route) {
  if (!route) return null
  const path = route.replace(/\/+$/, '') || '/'
  let best = null
  for (const group of adminTree) {
    for (const item of group.items) {
      if (path === item.route || path.startsWith(item.route + '/')) {
        if (!best || item.route.length > best.item.route.length) best = { group, item }
      }
    }
  }
  return best
}
